import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { ExternalLink, Plus, Trash2, Save, Check, Building2, Users, Calendar, Clock, Banknote, BookOpen, ToggleRight } from 'lucide-react'
import api from '../lib/api'
import { useModules } from '../lib/modules'
import ModulesPanel from '../components/ModulesPanel'

// ── Helpers ────────────────────────────────────────────────────────
function Field({ label, description, children }: { label: string; description?: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-6 py-3 border-b border-gray-50 last:border-0">
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-gray-700">{label}</div>
        {description && <div className="text-xs text-gray-400 mt-0.5">{description}</div>}
      </div>
      <div className="flex-shrink-0">{children}</div>
    </div>
  )
}
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="px-6 py-3 border-b border-gray-100 bg-gray-50">
        <h2 className="font-semibold text-gray-800 text-sm">{title}</h2>
      </div>
      <div className="px-6 py-2">{children}</div>
    </div>
  )
}
function NumField({ value, onChange, unit, min, max, step }: {
  value: number; onChange: (v: number) => void; unit?: string; min?: number; max?: number; step?: number
}) {
  return (
    <div className="flex items-center gap-2">
      <input type="number" value={value} min={min} max={max} step={step||1}
        onChange={e => onChange(+e.target.value)}
        className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm w-20 focus:outline-none focus:ring-2 focus:ring-blue-500" />
      {unit && <span className="text-sm text-gray-500">{unit}</span>}
    </div>
  )
}
function Toggle({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label?: string }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer">
      <button type="button" onClick={() => onChange(!checked)}
        className={`relative w-10 h-6 rounded-full transition-colors ${checked ? 'bg-blue-600' : 'bg-gray-200'}`}>
        <span className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${checked ? 'translate-x-4' : ''}`} />
      </button>
      {label && <span className="text-sm text-gray-600">{label}</span>}
    </label>
  )
}
function Input({ value, onChange, placeholder, width, maxLength }: {
  value: string; onChange: (v: string) => void; placeholder?: string; width?: string; maxLength?: number
}) {
  return (
    <input value={value} onChange={e => onChange(e.target.value)}
      placeholder={placeholder} maxLength={maxLength}
      className={`border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${width||'w-56'}`} />
  )
}

// ── Default values ─────────────────────────────────────────────────
const DEF_BADGE = {
  requirePin: true, requirePhoto: false, geoEnabled: false, geoRadiusM: 200,
  roundingMode: 'nearest5', lateToleranceMin: 5,
  autoBreakAfterMin: 360, autoBreakDurationMin: 30, alertOverMin: 600, allowMobileClocking: true
}
const DEF_PLANNING = {
  granularityMin: 15, lockPast: true, autoPublishDaysAhead: 0,
  warnGapUnderH: 1, defaultShiftColor: '#3B82F6'
}
const DEF_HR = {
  contractTypes: ['CDI','CDD','Intérim','Stage','Apprentissage','Saisonnier'],
  categories: ['Employé','Agent de maîtrise','Cadre'],
  conventionLevels: 'E1,E2,E3,AM1,AM2,Cadre',
  documentTemplates: [] as string[]
}
const DEF_CONVENTION = {
  idcc: '1505', name: 'Commerce de détail alimentaire non spécialisé',
  overtimeThreshold1H: 35, overtimeThreshold2H: 43,
  nightStart: '21:00', nightEnd: '06:00',
  night1Rate: 5, night2Rate: 20, sundayRate: 100, may1Rate: 100,
  paidLeaveDays: 25, rttDays: 0,
  mealVoucherValue: 8.50, mealVoucherEmployerShare: 60,
  customHolidays: [] as { date: string; name: string; active: boolean }[]
}

const TABS = [
  { id: 'org',        label: 'Organisation',  icon: Building2 },
  { id: 'hr',         label: 'RH',            icon: Users },
  { id: 'planning',   label: 'Planning',      icon: Calendar },
  { id: 'badge',      label: 'Badgeuse',      icon: Clock },
  { id: 'payroll',    label: 'Paie',          icon: Banknote },
  { id: 'convention', label: 'Convention',    icon: BookOpen },
  { id: 'modules',    label: 'Modules',       icon: ToggleRight },
] as const
// Onglets qui n'ont de sens qu'avec le module RH (masqués sinon)
const RH_TABS = ['hr', 'badge', 'payroll', 'convention']
type TabId = typeof TABS[number]['id']

// ─────────────────────────────────────────────────────────────────
export default function Settings() {
  const [tab, setTab]     = useState<TabId>('org')
  const { modules } = useModules()
  const visibleTabs = TABS.filter(t => modules.rh || !RH_TABS.includes(t.id))
  const [saved, setSaved] = useState(false)
  const [saving, setSaving] = useState(false)

  // ── State sections ──────────────────────────────────────────────
  const [org, setOrg] = useState({
    name: '', siren: '', siret: '', timezone: 'Europe/Paris',
    country: 'FR', conventionCode: '1505',
    nafCode: '', caisseConges: '', mutuelle: '', prevoyance: '', language: 'fr'
  })
  const [badge, setBadge]           = useState(DEF_BADGE)
  const [planning, setPlanning]     = useState(DEF_PLANNING)
  const [hr, setHr]                 = useState(DEF_HR)
  const [conv, setConv]             = useState(DEF_CONVENTION)
  const [newHoliday, setNewHoliday] = useState({ date: '', name: '' })
  const [newContractType, setNewContractType] = useState('')

  const load = useCallback(async () => {
    try {
      const r = await api.get('/organization')
      const d = r.data
      setOrg(o => ({
        ...o,
        name: d.name||'', siren: d.siren||'', siret: d.siret||'',
        timezone: d.timezone||'Europe/Paris', country: d.country||'FR',
        conventionCode: d.conventionCode||'1505',
        nafCode: d.nafCode||'', caisseConges: d.caisseConges||'',
        mutuelle: d.mutuelle||'', prevoyance: d.prevoyance||'', language: d.language||'fr'
      }))
      if (d.badgeSettings)      setBadge(bs => ({ ...DEF_BADGE, ...d.badgeSettings }))
      if (d.planningSettings)   setPlanning(ps => ({ ...DEF_PLANNING, ...d.planningSettings }))
      if (d.hrSettings)         setHr(hs => ({ ...DEF_HR, ...d.hrSettings }))
      if (d.conventionSettings) setConv(cs => ({ ...DEF_CONVENTION, ...d.conventionSettings }))
    } catch {}
  }, [])

  useEffect(() => { load() }, [load])

  // ── Save current tab ────────────────────────────────────────────
  async function save() {
    setSaving(true)
    try {
      const payload: any = {
        name: org.name, siren: org.siren, siret: org.siret, timezone: org.timezone,
        country: org.country, conventionCode: org.conventionCode,
        nafCode: org.nafCode, caisseConges: org.caisseConges,
        mutuelle: org.mutuelle, prevoyance: org.prevoyance, language: org.language,
        badgeSettings: badge, planningSettings: planning,
        hrSettings: hr, conventionSettings: conv
      }
      await api.put('/organization', payload)
      setSaved(true); setTimeout(() => setSaved(false), 2500)
    } catch {}
    setSaving(false)
  }

  // ── HR helpers ──────────────────────────────────────────────────
  function addContractType() {
    if (!newContractType.trim()) return
    setHr(h => ({ ...h, contractTypes: [...h.contractTypes, newContractType.trim()] }))
    setNewContractType('')
  }
  function removeContractType(ct: string) {
    setHr(h => ({ ...h, contractTypes: h.contractTypes.filter(c => c !== ct) }))
  }

  // ── Holiday helpers ─────────────────────────────────────────────
  function addHoliday() {
    if (!newHoliday.date || !newHoliday.name) return
    setConv(c => ({ ...c, customHolidays: [...c.customHolidays, { ...newHoliday, active: true }] }))
    setNewHoliday({ date: '', name: '' })
  }

  const FRENCH_HOLIDAYS = [
    { date: `${new Date().getFullYear()}-01-01`, name: "Jour de l'an" },
    { date: `${new Date().getFullYear()}-05-01`, name: 'Fête du Travail' },
    { date: `${new Date().getFullYear()}-05-08`, name: 'Victoire 1945' },
    { date: `${new Date().getFullYear()}-07-14`, name: 'Fête Nationale' },
    { date: `${new Date().getFullYear()}-08-15`, name: 'Assomption' },
    { date: `${new Date().getFullYear()}-11-01`, name: 'Toussaint' },
    { date: `${new Date().getFullYear()}-11-11`, name: 'Armistice' },
    { date: `${new Date().getFullYear()}-12-25`, name: 'Noël' },
  ]

  return (
    <div className="flex h-full overflow-hidden">
      {/* ── Sidebar tabs ──────────────────────────────────────────── */}
      <div className="w-52 bg-gray-50 border-r border-gray-200 flex flex-col flex-shrink-0 py-4 gap-1 px-2">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide px-2 mb-2">Paramétrage</p>
        {visibleTabs.map(t => {
          const Icon = t.icon
          return (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors text-left ${
                tab === t.id ? 'bg-white shadow-sm border border-gray-200 text-blue-600' : 'text-gray-600 hover:bg-white hover:text-gray-900'
              }`}>
              <Icon size={16} className={tab === t.id ? 'text-blue-500' : 'text-gray-400'} />
              {t.label}
            </button>
          )
        })}
      </div>

      {/* ── Main content ─────────────────────────────────────────── */}
      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-2xl space-y-5">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-gray-900">{TABS.find(t => t.id === tab)?.label}</h1>
              <p className="text-sm text-gray-500 mt-0.5">Paramètres {TABS.find(t => t.id === tab)?.label.toLowerCase()}</p>
            </div>
            {tab !== 'modules' && <button onClick={save} disabled={saving}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all disabled:opacity-60 ${
                saved ? 'bg-green-500 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}>
              {saved ? <><Check size={14} /> Enregistré</> : <><Save size={14} /> Enregistrer</>}
            </button>}
          </div>

          {/* ── Tab: Modules (allumer / éteindre des parties de l'appli) ── */}
          {tab === 'modules' && <ModulesPanel />}

          {/* ── Tab: Organisation ───────────────────────────────── */}
          {tab === 'org' && <>
            <Section title="Identité entreprise">
              <Field label="Raison sociale">
                <Input value={org.name} onChange={v => setOrg(o => ({ ...o, name: v }))} />
              </Field>
              <Field label="SIREN" description="9 chiffres">
                <Input value={org.siren} onChange={v => setOrg(o => ({ ...o, siren: v }))} width="w-36" maxLength={9} placeholder="000000000" />
              </Field>
              <Field label="SIRET" description="14 chiffres — requis pour DSN">
                <Input value={org.siret} onChange={v => setOrg(o => ({ ...o, siret: v }))} width="w-48" maxLength={14} placeholder="00000000000000" />
              </Field>
              <Field label="Code NAF / APE">
                <Input value={org.nafCode} onChange={v => setOrg(o => ({ ...o, nafCode: v }))} width="w-28" placeholder="4711D" />
              </Field>
              <Field label="Langue">
                <select value={org.language} onChange={e => setOrg(o => ({ ...o, language: e.target.value }))}
                  className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm w-40 focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="fr">Français</option>
                  <option value="en">English</option>
                </select>
              </Field>
              <Field label="Pays">
                <select value={org.country} onChange={e => setOrg(o => ({ ...o, country: e.target.value }))}
                  className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm w-40 focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="FR">France</option>
                  <option value="BE">Belgique</option>
                  <option value="LU">Luxembourg</option>
                  <option value="CH">Suisse</option>
                </select>
              </Field>
              <Field label="Fuseau horaire">
                <select value={org.timezone} onChange={e => setOrg(o => ({ ...o, timezone: e.target.value }))}
                  className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm w-56 focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="Europe/Paris">Europe/Paris (UTC+1/+2)</option>
                  <option value="Europe/London">Europe/London (UTC+0/+1)</option>
                  <option value="Europe/Brussels">Europe/Brussels (UTC+1/+2)</option>
                  <option value="America/Guadeloupe">Guadeloupe (UTC−4)</option>
                  <option value="Indian/Reunion">La Réunion (UTC+4)</option>
                  <option value="America/Martinique">Martinique (UTC−4)</option>
                </select>
              </Field>
            </Section>
            <Section title="Organismes sociaux">
              <Field label="Caisse de congés payés">
                <Input value={org.caisseConges} onChange={v => setOrg(o => ({ ...o, caisseConges: v }))} placeholder="Ex: CGSCOP" />
              </Field>
              <Field label="Mutuelle">
                <Input value={org.mutuelle} onChange={v => setOrg(o => ({ ...o, mutuelle: v }))} placeholder="Nom de la mutuelle" />
              </Field>
              <Field label="Prévoyance">
                <Input value={org.prevoyance} onChange={v => setOrg(o => ({ ...o, prevoyance: v }))} placeholder="Nom de l'organisme" />
              </Field>
              <Field label="Convention collective (IDCC)" description="Appliquée à tous les établissements">
                <Input value={org.conventionCode} onChange={v => setOrg(o => ({ ...o, conventionCode: v }))} width="w-24" placeholder="1505" />
              </Field>
            </Section>
            <div className="bg-gray-50 rounded-xl border border-gray-200 px-5 py-4 flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-700">Intégrations paie</p>
                <p className="text-xs text-gray-400 mt-0.5">Gérez les connecteurs depuis le journal d'intégration</p>
              </div>
              <Link to="/integration-logs" className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors">
                Gérer <ExternalLink size={14} />
              </Link>
            </div>
          </>}

          {/* ── Tab: RH ─────────────────────────────────────────── */}
          {tab === 'hr' && <>
            <Section title="Types de contrats">
              <div className="py-2 space-y-2">
                {hr.contractTypes.map((ct, i) => (
                  <div key={i} className="flex items-center justify-between gap-2">
                    <span className="text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-lg px-3 py-1.5 flex-1">{ct}</span>
                    <button onClick={() => removeContractType(ct)} className="p-1.5 text-gray-400 hover:text-red-500 rounded transition-colors">
                      <Trash2 size={13} />
                    </button>
                  </div>
                ))}
                <div className="flex gap-2 pt-2">
                  <input value={newContractType} onChange={e => setNewContractType(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') addContractType() }}
                    placeholder="Nouveau type de contrat"
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  <button onClick={addContractType} disabled={!newContractType.trim()}
                    className="flex items-center gap-1 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white text-sm px-3 py-1.5 rounded-lg transition-colors">
                    <Plus size={14} />
                  </button>
                </div>
              </div>
            </Section>
            <Section title="Catégories socioprofessionnelles">
              <div className="py-2 space-y-2">
                {hr.categories.map((cat, i) => (
                  <div key={i} className="flex items-center justify-between gap-2">
                    <span className="text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-lg px-3 py-1.5 flex-1">{cat}</span>
                    <button onClick={() => setHr(h => ({ ...h, categories: h.categories.filter((_, j) => j !== i) }))}
                      className="p-1.5 text-gray-400 hover:text-red-500 rounded transition-colors"><Trash2 size={13} /></button>
                  </div>
                ))}
                <div className="flex gap-2 pt-2">
                  <input placeholder="Nouvelle catégorie"
                    onKeyDown={e => { if (e.key === 'Enter' && (e.target as HTMLInputElement).value) { setHr(h => ({ ...h, categories: [...h.categories, (e.target as HTMLInputElement).value] }));(e.target as HTMLInputElement).value='' } }}
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  <span className="text-xs text-gray-400 flex items-center">Entrée pour ajouter</span>
                </div>
              </div>
            </Section>
            <Section title="Niveaux de classification">
              <Field label="Niveaux" description="Séparés par des virgules (ex: E1, E2, AM1, Cadre)">
                <input value={hr.conventionLevels} onChange={e => setHr(h => ({ ...h, conventionLevels: e.target.value }))}
                  className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm w-72 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </Field>
            </Section>
          </>}

          {/* ── Tab: Planning ────────────────────────────────────── */}
          {tab === 'planning' && <>
            <Section title="Grille et affichage">
              <Field label="Granularité de la grille" description="Pas de temps minimum pour les créneaux">
                <select value={planning.granularityMin} onChange={e => setPlanning(p => ({ ...p, granularityMin: +e.target.value }))}
                  className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm w-40 focus:outline-none focus:ring-2 focus:ring-blue-500">
                  {[5,10,15,30,60].map(v => <option key={v} value={v}>{v} minutes</option>)}
                </select>
              </Field>
              <Field label="Couleur par défaut des shifts">
                <div className="flex items-center gap-2">
                  <input type="color" value={planning.defaultShiftColor}
                    onChange={e => setPlanning(p => ({ ...p, defaultShiftColor: e.target.value }))}
                    className="w-10 h-9 border border-gray-300 rounded-lg cursor-pointer" />
                  <span className="text-sm font-mono text-gray-500">{planning.defaultShiftColor}</span>
                </div>
              </Field>
            </Section>
            <Section title="Règles de publication">
              <Field label="Publication automatique" description="Publier les shifts N jours à l'avance (0 = désactivé)">
                <NumField value={planning.autoPublishDaysAhead} onChange={v => setPlanning(p => ({ ...p, autoPublishDaysAhead: v }))} unit="jours" min={0} max={30} />
              </Field>
              <Field label="Verrouiller le passé" description="Empêcher la modification des shifts déjà passés">
                <Toggle checked={planning.lockPast} onChange={v => setPlanning(p => ({ ...p, lockPast: v }))} />
              </Field>
            </Section>
            <Section title="Alertes">
              <Field label="Alerte écart inter-shifts" description="Avertir si l'écart entre deux shifts est inférieur à">
                <NumField value={planning.warnGapUnderH} onChange={v => setPlanning(p => ({ ...p, warnGapUnderH: v }))} unit="h" min={0} max={24} />
              </Field>
            </Section>
          </>}

          {/* ── Tab: Badgeuse ────────────────────────────────────── */}
          {tab === 'badge' && <>
            <Section title="Sécurité & accès">
              <Field label="Code PIN obligatoire" description="L'employé doit saisir son PIN pour pointer">
                <Toggle checked={badge.requirePin} onChange={v => setBadge(b => ({ ...b, requirePin: v }))} />
              </Field>
              <Field label="Photo obligatoire" description="Prendre une photo lors du pointage">
                <Toggle checked={badge.requirePhoto} onChange={v => setBadge(b => ({ ...b, requirePhoto: v }))} />
              </Field>
              <Field label="Pointage mobile autorisé">
                <Toggle checked={badge.allowMobileClocking} onChange={v => setBadge(b => ({ ...b, allowMobileClocking: v }))} />
              </Field>
            </Section>
            <Section title="Géolocalisation">
              <Field label="Géolocalisation activée" description="Vérifier la position lors du pointage">
                <Toggle checked={badge.geoEnabled} onChange={v => setBadge(b => ({ ...b, geoEnabled: v }))} />
              </Field>
              <Field label="Rayon autorisé" description="Distance maximale depuis l'établissement">
                <NumField value={badge.geoRadiusM} onChange={v => setBadge(b => ({ ...b, geoRadiusM: v }))} unit="m" min={10} max={5000} step={10} />
              </Field>
            </Section>
            <Section title="Règles d'arrondi et pauses">
              <Field label="Arrondi du pointage">
                <select value={badge.roundingMode} onChange={e => setBadge(b => ({ ...b, roundingMode: e.target.value }))}
                  className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm w-56 focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="exact">Aucun arrondi (exact)</option>
                  <option value="nearest5">Arrondi à 5 min le plus proche</option>
                  <option value="down5">Arrondi au quart d'heure inférieur</option>
                  <option value="up5">Arrondi au quart d'heure supérieur</option>
                </select>
              </Field>
              <Field label="Tolérance retard">
                <NumField value={badge.lateToleranceMin} onChange={v => setBadge(b => ({ ...b, lateToleranceMin: v }))} unit="min" min={0} max={60} />
              </Field>
              <Field label="Pause automatique" description="Insérer une pause après N minutes de travail">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm text-gray-500">Après</span>
                  <NumField value={badge.autoBreakAfterMin} onChange={v => setBadge(b => ({ ...b, autoBreakAfterMin: v }))} unit="min" min={0} step={30} />
                  <span className="text-sm text-gray-500">→ pause de</span>
                  <NumField value={badge.autoBreakDurationMin} onChange={v => setBadge(b => ({ ...b, autoBreakDurationMin: v }))} unit="min" min={0} step={5} />
                </div>
              </Field>
              <Field label="Alerte dépassement" description="Notification si un pointage dépasse">
                <NumField value={badge.alertOverMin} onChange={v => setBadge(b => ({ ...b, alertOverMin: v }))} unit={`min (${(badge.alertOverMin/60).toFixed(1)}h)`} min={0} step={30} />
              </Field>
            </Section>
          </>}

          {/* ── Tab: Paie ────────────────────────────────────────── */}
          {tab === 'payroll' && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-700">
                Le paramétrage paie (rubriques, seuils heures sup, taux nuit/dimanche, 1er mai) est géré dans le module
                {' '}<strong>Préparation paie → Paramètres</strong>.
              </div>
              <Section title="Liens rapides">
                <div className="py-2 space-y-3">
                  <Link to="/payroll" className="flex items-center justify-between hover:bg-gray-50 rounded-lg px-3 py-2 -mx-3 transition-colors group">
                    <div>
                      <p className="text-sm font-medium text-gray-800">Rubriques de paie</p>
                      <p className="text-xs text-gray-400">Mapping codes rubriques (SILAE, Sage, DSN…)</p>
                    </div>
                    <ExternalLink size={14} className="text-gray-400 group-hover:text-blue-600 transition-colors" />
                  </Link>
                  <Link to="/integration-logs" className="flex items-center justify-between hover:bg-gray-50 rounded-lg px-3 py-2 -mx-3 transition-colors group">
                    <div>
                      <p className="text-sm font-medium text-gray-800">Connecteurs paie</p>
                      <p className="text-xs text-gray-400">Payfit, Silae, Sage, ADP, Cegid, DSN…</p>
                    </div>
                    <ExternalLink size={14} className="text-gray-400 group-hover:text-blue-600 transition-colors" />
                  </Link>
                </div>
              </Section>
            </div>
          )}

          {/* ── Tab: Convention ──────────────────────────────────── */}
          {tab === 'convention' && <>
            <Section title="Identifiant convention">
              <Field label="IDCC" description="Code identifiant de la convention collective">
                <div className="flex items-center gap-2">
                  <Input value={conv.idcc} onChange={v => setConv(c => ({ ...c, idcc: v }))} width="w-24" placeholder="1505" />
                  <span className="inline-block bg-blue-100 text-blue-700 text-xs font-medium px-2 py-1 rounded-full">IDCC {conv.idcc}</span>
                </div>
              </Field>
              <Field label="Intitulé">
                <Input value={conv.name} onChange={v => setConv(c => ({ ...c, name: v }))} width="w-72" />
              </Field>
            </Section>
            <Section title="Temps de travail">
              <Field label="Seuil heures sup +25%" description="Seuil hebdo à partir duquel commence le +25%">
                <NumField value={conv.overtimeThreshold1H} onChange={v => setConv(c => ({ ...c, overtimeThreshold1H: v }))} unit="h" min={0} max={48} />
              </Field>
              <Field label="Seuil heures sup +50%" description="Seuil hebdo à partir duquel commence le +50%">
                <NumField value={conv.overtimeThreshold2H} onChange={v => setConv(c => ({ ...c, overtimeThreshold2H: v }))} unit="h" min={0} max={60} />
              </Field>
              <Field label="Congés payés légaux">
                <NumField value={conv.paidLeaveDays} onChange={v => setConv(c => ({ ...c, paidLeaveDays: v }))} unit="jours/an" min={0} max={60} />
              </Field>
              <Field label="Jours RTT">
                <NumField value={conv.rttDays} onChange={v => setConv(c => ({ ...c, rttDays: v }))} unit="jours/an" min={0} max={30} />
              </Field>
            </Section>
            <Section title="Majorations">
              <Field label="Plage horaire de nuit">
                <div className="flex items-center gap-2">
                  <input value={conv.nightStart} onChange={e => setConv(c => ({ ...c, nightStart: e.target.value }))}
                    className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm w-24 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  <span className="text-gray-400">→</span>
                  <input value={conv.nightEnd} onChange={e => setConv(c => ({ ...c, nightEnd: e.target.value }))}
                    className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm w-24 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              </Field>
              <Field label="Majoration nuit tranche 1">
                <NumField value={conv.night1Rate} onChange={v => setConv(c => ({ ...c, night1Rate: v }))} unit="%" min={0} max={100} />
              </Field>
              <Field label="Majoration nuit tranche 2">
                <NumField value={conv.night2Rate} onChange={v => setConv(c => ({ ...c, night2Rate: v }))} unit="%" min={0} max={100} />
              </Field>
              <Field label="Majoration travail dimanche">
                <NumField value={conv.sundayRate} onChange={v => setConv(c => ({ ...c, sundayRate: v }))} unit="%" min={0} max={200} />
              </Field>
              <Field label="Majoration 1er mai">
                <NumField value={conv.may1Rate} onChange={v => setConv(c => ({ ...c, may1Rate: v }))} unit="%" min={0} max={200} />
              </Field>
            </Section>
            <Section title="Tickets restaurant">
              <Field label="Valeur faciale">
                <NumField value={conv.mealVoucherValue} onChange={v => setConv(c => ({ ...c, mealVoucherValue: v }))} unit="€" min={0} step={0.01} />
              </Field>
              <Field label="Part employeur">
                <NumField value={conv.mealVoucherEmployerShare} onChange={v => setConv(c => ({ ...c, mealVoucherEmployerShare: v }))} unit="%" min={50} max={60} />
              </Field>
            </Section>
            <Section title="Jours fériés personnalisés">
              <div className="py-2">
                <p className="text-xs text-gray-500 mb-3">Les 11 jours fériés légaux français sont calculés automatiquement. Ajoutez ici des jours supplémentaires.</p>
                <div className="space-y-2 mb-3">
                  {conv.customHolidays.map((h, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <input type="checkbox" checked={h.active}
                        onChange={e => setConv(c => ({ ...c, customHolidays: c.customHolidays.map((x, j) => j===i ? { ...x, active: e.target.checked } : x) }))}
                        className="w-4 h-4 text-blue-600 rounded" />
                      <span className={`text-sm flex-1 ${h.active ? 'text-gray-800' : 'text-gray-400'}`}>{h.name}</span>
                      <span className="text-xs font-mono text-gray-400">{h.date}</span>
                      <button onClick={() => setConv(c => ({ ...c, customHolidays: c.customHolidays.filter((_, j) => j !== i) }))}
                        className="text-gray-300 hover:text-red-500 transition-colors"><Trash2 size={13} /></button>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input type="date" value={newHoliday.date} onChange={e => setNewHoliday(h => ({ ...h, date: e.target.value }))}
                    className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  <input type="text" value={newHoliday.name} placeholder="Nom du jour férié"
                    onChange={e => setNewHoliday(h => ({ ...h, name: e.target.value }))}
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  <button onClick={addHoliday} disabled={!newHoliday.date || !newHoliday.name}
                    className="flex items-center gap-1 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white text-sm px-3 py-1.5 rounded-lg transition-colors">
                    <Plus size={14} />
                  </button>
                </div>
              </div>
            </Section>
          </>}
        </div>
      </div>
    </div>
  )
}
